import { baseApi } from '@/features/api/baseApi';
import { type RequestQuery } from '@/features/api/directus';
import {
  type ReadOneQueryArg,
  buildCountFn,
  buildCreateFn,
  buildDeleteFn,
  buildDeleteManyFn,
  buildReadManyFn,
  buildReadOneFn,
  buildUpdateFn,
  buildUpdateManyFn,
} from '@/features/api/endpoints/createDirectusEndpoints';
import type { MenuNavigation } from '@/models/types';

const menuNavigationApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ['menuNavigation', 'menuNavigations'],
  })
  .injectEndpoints({
    endpoints: builder => ({
      getMenuNavigations: builder.query<MenuNavigation[], RequestQuery<MenuNavigation>>({
        queryFn: buildReadManyFn<MenuNavigation>('MenuNavigation'),
        keepUnusedDataFor: 3600, // 1 hour
        providesTags: ['menuNavigations'],
      }),
      getMenuNavigation: builder.query<MenuNavigation, ReadOneQueryArg<MenuNavigation, number>>({
        queryFn: buildReadOneFn<MenuNavigation, number>('MenuNavigation'),
        providesTags: ['menuNavigation'],
      }),
      getMenuNavigationCount: builder.query<number, RequestQuery<MenuNavigation> | undefined>({
        queryFn: buildCountFn<MenuNavigation>('MenuNavigation'),
        providesTags: ['menuNavigations'],
      }),
      createMenuNavigation: builder.mutation<MenuNavigation, Partial<MenuNavigation>>({
        queryFn: buildCreateFn<MenuNavigation>('MenuNavigation'),
        invalidatesTags: ['menuNavigation', 'menuNavigations'],
      }),
      updateMenuNavigation: builder.mutation<
        MenuNavigation,
        { id: number; body: Partial<MenuNavigation> }
      >({
        queryFn: buildUpdateFn<MenuNavigation, number>('MenuNavigation'),
        invalidatesTags: ['menuNavigation', 'menuNavigations'],
      }),
      updateMenuNavigations: builder.mutation<
        MenuNavigation[],
        { ids: number[]; body: Partial<MenuNavigation> }
      >({
        queryFn: buildUpdateManyFn<MenuNavigation, number>('MenuNavigation'),
        invalidatesTags: ['menuNavigation', 'menuNavigations'],
      }),
      deleteMenuNavigation: builder.mutation<void, number>({
        queryFn: buildDeleteFn<number>('MenuNavigation'),
        invalidatesTags: ['menuNavigation', 'menuNavigations'],
      }),
      deleteMenuNavigations: builder.mutation<void, number[]>({
        queryFn: buildDeleteManyFn<number>('MenuNavigation'),
        invalidatesTags: ['menuNavigation', 'menuNavigations'],
      }),
    }),
  });

export const {
  useGetMenuNavigationsQuery,
  useGetMenuNavigationQuery,
  useGetMenuNavigationCountQuery,
  useCreateMenuNavigationMutation,
  useUpdateMenuNavigationMutation,
  useUpdateMenuNavigationsMutation,
  useDeleteMenuNavigationMutation,
  useDeleteMenuNavigationsMutation,
} = menuNavigationApi;
